import { getHSLAColor } from "@/helpers/style.helper.ts";
import {
  Chart as ChartJS,
  type ChartType,
  type ScriptableTooltipContext,
  type TooltipYAlignment,
} from "chart.js";

export const reportingTooltipCaretPadding = 6;

export const getReportingTooltipYAlign = <TType extends ChartType>({
  chart,
  tooltip,
}: ScriptableTooltipContext<TType>): TooltipYAlignment => {
  const activeElements = chart.getActiveElements();
  const anchorY = activeElements.length
    ? Math.min(
        ...activeElements.map(
          ({ element }) => element.tooltipPosition(false).y ?? chart.chartArea.top
        )
      )
    : tooltip.caretY;
  const estimatedHeight = 32 + activeElements.length * 18;
  const tooltipHeight = Math.max(tooltip.height || 0, estimatedHeight);

  return anchorY - tooltipHeight - reportingTooltipCaretPadding >= chart.chartArea.top
    ? "bottom"
    : "top";
};

export const hoverHighlightPlugin = {
  id: "hoverHighlight",
  beforeDatasetsDraw: (chart: ChartJS) => {
    const { ctx, chartArea, scales, tooltip } = chart;
    if (!chartArea || !scales.x || !tooltip?.dataPoints?.length) return;

    const activeIndex = tooltip.dataPoints[0].dataIndex;
    const xScale = scales.x;
    const ticks = xScale.ticks;
    if (!ticks || ticks.length === 0) return;

    const tickWidth = (chartArea.right - chartArea.left) / ticks.length;
    const x = xScale.getPixelForTick(activeIndex) - tickWidth / 2;

    ctx.save();
    ctx.fillStyle = getHSLAColor("--color-dark", 0.1);
    ctx.fillRect(x, chartArea.top, tickWidth, chartArea.bottom - chartArea.top);
    ctx.restore();
  },
};

const strokeGridLines = (
  ctx: CanvasRenderingContext2D,
  segments: [number, number, number, number][],
  dash: number[] = []
) => {
  ctx.save();
  ctx.strokeStyle = getHSLAColor("--color-dark", 0.1);
  ctx.lineWidth = 1;
  ctx.setLineDash(dash);
  for (const [x1, y1, x2, y2] of segments) {
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
  }
  ctx.restore();
};

export const verticalLinesPlugin = {
  id: "verticalLines",
  afterDatasetsDraw: (chart: ChartJS) => {
    const { ctx, chartArea, scales } = chart;
    if (!chartArea || !scales.x) return;

    const xScale = scales.x;
    const ticks = xScale.ticks;
    if (!ticks || ticks.length < 2) return;

    const segments = ticks.slice(1).map((_, i): [number, number, number, number] => {
      const x = (xScale.getPixelForTick(i) + xScale.getPixelForTick(i + 1)) / 2;
      return [x, chartArea.top, x, chartArea.bottom];
    });
    strokeGridLines(ctx, segments);
  },
};

export const horizontalLinesPlugin = {
  id: "horizontalLines",
  afterDatasetsDraw: (chart: ChartJS) => {
    const { ctx, chartArea, scales } = chart;
    if (!chartArea || !scales.y) return;

    const yScale = scales.y;
    const ticks = yScale.ticks;
    if (!ticks || ticks.length === 0) return;

    const segments = ticks.map((_, i): [number, number, number, number] => {
      const y = yScale.getPixelForTick(i);
      return [chartArea.left, y, chartArea.right, y];
    });
    strokeGridLines(ctx, segments, [4, 4]);
  },
};

export const lineShadowPlugin = {
  id: "lineShadow",
  beforeDatasetsDraw: (chart: ChartJS) => {
    chart.ctx.shadowColor = "#FFFFFF";
    chart.ctx.shadowBlur = 0;
    chart.ctx.shadowOffsetX = 0;
    chart.ctx.shadowOffsetY = 2;
  },
  afterDatasetsDraw: (chart: ChartJS) => {
    chart.ctx.shadowColor = "transparent";
    chart.ctx.shadowOffsetY = 0;
  },
};

export const hoverPointPlugin = {
  id: "hoverPoint",
  afterDraw: (chart: ChartJS) => {
    const { ctx, tooltip } = chart;
    if (!tooltip?.dataPoints?.length) return;

    const dataPoint = tooltip.dataPoints[0];
    const x = dataPoint.element.x;
    const y = dataPoint.element.y;

    ctx.save();
    ctx.beginPath();
    ctx.arc(x, y, 7, 0, Math.PI * 2);
    ctx.fillStyle = "#FFFFFF";
    ctx.fill();
    ctx.lineWidth = 3;
    ctx.strokeStyle = getHSLAColor("--color-dark", 1);
    ctx.stroke();
    ctx.restore();
  },
};
